import { useState } from "react";
import { updatePrice, updateReorderPoint, type PriceHistoryEntry, type Product } from "./catalog";
import { formatMoney, parsePrice, parseReorderPoint } from "./money";
import { withSpan } from "./otel";
import { signIn } from "./session";

// Save-in-flight state for one field. Price and reorder-point each get
// their own, so editing one doesn't disable the other (pattern: storefront-ui's
// CartDrawer.tsx `Phase` union for its checkout button).
type FieldPhase = { name: "idle" } | { name: "saving" } | { name: "error"; message: string };

type History = PriceHistoryEntry[] | "loading" | "error";

export function CatalogRow({
  product,
  expanded,
  history,
  onToggle,
  onUpdated,
  onPriceSaved,
}: {
  product: Product;
  expanded: boolean;
  history: History | undefined;
  onToggle: () => void;
  onUpdated: (product: Product) => void;
  // Fired after a successful price save so the parent can refresh history
  // (a new entry lands on top — the price PUT itself doesn't return it).
  onPriceSaved: (sku: string) => void;
}) {
  const [priceInput, setPriceInput] = useState(String(product.unitPrice));
  const [reorderInput, setReorderInput] = useState(String(product.reorderPoint));
  const [pricePhase, setPricePhase] = useState<FieldPhase>({ name: "idle" });
  const [reorderPhase, setReorderPhase] = useState<FieldPhase>({ name: "idle" });

  async function savePrice() {
    const value = parsePrice(priceInput);
    if (value === null) {
      setPricePhase({ name: "error", message: "must be > 0, with at most 2 decimal places" });
      return;
    }

    setPricePhase({ name: "saving" });
    const result = await withSpan(
      "catalog.price_change",
      { "catalog.sku": product.sku },
      () => updatePrice(product.sku, value),
    );

    if (result.kind === "ok") {
      setPricePhase({ name: "idle" });
      onUpdated(result.value);
      onPriceSaved(product.sku);
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setPricePhase({ name: "error", message: result.message });
    }
  }

  async function saveReorderPoint() {
    const value = parseReorderPoint(reorderInput);
    if (value === null) {
      setReorderPhase({ name: "error", message: "must be a whole number, 0 or more" });
      return;
    }

    setReorderPhase({ name: "saving" });
    const result = await withSpan(
      "catalog.reorder_point_change",
      { "catalog.sku": product.sku },
      () => updateReorderPoint(product.sku, value),
    );

    if (result.kind === "ok") {
      setReorderPhase({ name: "idle" });
      onUpdated(result.value);
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setReorderPhase({ name: "error", message: result.message });
    }
  }

  return (
    <>
      <tr className="catalog-row">
        <td>{product.sku}</td>
        <td>{product.name}</td>
        <td>{product.category}</td>
        <td className="catalog-num">{formatMoney(product.unitPrice)}</td>
        <td className="catalog-num">{product.reorderPoint}</td>
        <td>
          <button type="button" onClick={onToggle}>
            {expanded ? "Close" : "Edit"}
          </button>
        </td>
      </tr>

      {expanded && (
        <tr className="catalog-row-expand-row">
          <td colSpan={6}>
            <div className="catalog-row-expand">
              <div className="catalog-edit-field">
                <label htmlFor={`price-${product.sku}`}>Unit price (GBP)</label>
                <div className="catalog-edit-controls">
                  <input
                    id={`price-${product.sku}`}
                    type="text"
                    inputMode="decimal"
                    value={priceInput}
                    disabled={pricePhase.name === "saving"}
                    onChange={(e) => {
                      setPriceInput(e.target.value);
                      if (pricePhase.name === "error") setPricePhase({ name: "idle" });
                    }}
                  />
                  <button type="button" onClick={savePrice} disabled={pricePhase.name === "saving"}>
                    {pricePhase.name === "saving" ? "Saving…" : "Save price"}
                  </button>
                </div>
                {pricePhase.name === "error" && <p className="field-error">{pricePhase.message}</p>}
              </div>

              <div className="catalog-edit-field">
                <label htmlFor={`reorder-${product.sku}`}>Reorder point</label>
                <div className="catalog-edit-controls">
                  <input
                    id={`reorder-${product.sku}`}
                    type="text"
                    inputMode="numeric"
                    value={reorderInput}
                    disabled={reorderPhase.name === "saving"}
                    onChange={(e) => {
                      setReorderInput(e.target.value);
                      if (reorderPhase.name === "error") setReorderPhase({ name: "idle" });
                    }}
                  />
                  <button
                    type="button"
                    onClick={saveReorderPoint}
                    disabled={reorderPhase.name === "saving"}
                  >
                    {reorderPhase.name === "saving" ? "Saving…" : "Save reorder point"}
                  </button>
                </div>
                {reorderPhase.name === "error" && <p className="field-error">{reorderPhase.message}</p>}
              </div>

              <div className="catalog-history">
                <h3>Price history</h3>
                {history === undefined || history === "loading" ? (
                  <p className="status-message">Loading…</p>
                ) : history === "error" ? (
                  <p className="status-error">Couldn't load price history.</p>
                ) : history.length === 0 ? (
                  <p className="status-message">No price changes yet.</p>
                ) : (
                  <ul className="catalog-history-list">
                    {history.map((h) => (
                      <li key={h.effectiveFrom}>
                        <span className="catalog-history-date">
                          {new Date(h.effectiveFrom).toLocaleString()}
                        </span>
                        <span>
                          {formatMoney(h.previousUnitPrice)} → {formatMoney(h.unitPrice)}
                        </span>
                        <span className="catalog-history-by">{h.changedBy}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
